import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import HeroImage from '@/components/ui/HeroImage';
import MaterialIcon from '@/components/ui/MaterialIcon';
import AppHeader from '@/components/ui/AppHeader';
import { otpLoginSchema, otpVerifySchema, type OTPLoginFormData, type OTPVerifyFormData } from '@/utils/validators';
import { useAuthStore } from '@/store/authStore';
import { OTP } from '@/utils/constants';
import authService from '@/services/authService';
import { LoginMethod } from '@/types';

const OTPLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login: setAuthState } = useAuthStore();
  const [step, setStep] = useState<'request' | 'verify'>('request');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Timer for resend cooldown
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const requestForm = useForm<OTPLoginFormData>({
    resolver: zodResolver(otpLoginSchema),
  });

  const verifyForm = useForm<OTPVerifyFormData>({
    resolver: zodResolver(otpVerifySchema),
    defaultValues: { token: '', },
  });

  const onRequestOTP = async (data: OTPLoginFormData) => {
    setIsLoading(true);
    try {
      await authService.sendOTP({ identifier: data.email, type: 'login' });
      setEmail(data.email);
      verifyForm.setValue('token', '');
      setStep('verify');
      setCountdown(OTP.RESEND_COOLDOWN_SECONDS);
      toast.success('Code de vérification envoyé par email');
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de l\'envoi du code');
    } finally {
      setIsLoading(false);
    }
  };

  const onVerifyOTP = async (data: OTPVerifyFormData) => {
    setIsLoading(true);
    try {
      console.log('email', email);
      console.log(data);
      const response = await authService.login({
        email: email,
        otp: data.token,
        loginMethod: LoginMethod.OTP,
      });
      setAuthState(response.user, response.tokens.accessToken, response.tokens.refreshToken);
      toast.success('Connexion réussie!');
      navigate('/dashboard');
    } catch (error: any) {
      toast.error(error.message || 'Code OTP invalide');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    
    setIsLoading(true);
    try {
      await authService.sendOTP({ identifier: email, type: 'login' });
      setCountdown(OTP.RESEND_COOLDOWN_SECONDS);
      toast.success('Code OTP renvoyé');
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors du renvoi du code');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col mx-auto max-w-[480px] bg-white dark:bg-background-dark">
      {step === 'request' ? (
        <>
          <AppHeader title="Connexion par Email" backLabel="Connexion" onBack={() => navigate('/login')} />

          {/* Scrollable Content */}
          <div className="flex-1 flex flex-col">
            {/* Hero Image */}
            <HeroImage
              imageUrl="/images/login-hero.png"
              alt="Médecin en consultation avec une patiente dans un cabinet moderne"
              badge="ASSURANCE SANTÉ"
            />

            {/* Headline Text */}
            <div className="px-6 flex flex-col items-center">
              <h1 className="text-slate-900 dark:text-white tracking-tight text-2xl md:text-3xl font-bold leading-tight text-center mb-2">
                Connexion par{' '}
                <span className="text-emerald-600 dark:text-primary">code email</span>
              </h1>
              <p className="text-slate-500 dark:text-gray-400 text-base font-normal leading-relaxed text-center max-w-xs">
                Entrez votre adresse email pour recevoir un code de vérification
              </p>
            </div>

            {/* Form */}
            <form onSubmit={requestForm.handleSubmit(onRequestOTP)} className="px-6 flex flex-col gap-5 py-8">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-slate-700 dark:text-gray-300 ml-1">
                  Adresse email
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <MaterialIcon 
                      icon="mail" 
                      className="text-gray-400 group-focus-within:text-primary transition-colors"
                    />
                  </div>
                  <input
                    {...requestForm.register('email')}
                    type="email"
                    className="block w-full pl-11 pr-4 py-3.5 bg-gray-50 dark:bg-white/5 border-transparent focus:border-primary focus:ring-0 rounded-xl text-base text-slate-900 dark:text-white placeholder:text-gray-400 transition-all shadow-sm focus:bg-white dark:focus:bg-black/20"
                    placeholder="exemple@email.com"
                  />
                </div>
                {requestForm.formState.errors.email && (
                  <p className="text-sm text-red-600 ml-1">{requestForm.formState.errors.email.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="mt-4 w-full bg-primary hover:bg-[#25d360] active:scale-[0.98] transition-all duration-200 text-[#102216] text-base font-bold h-14 rounded-xl shadow-[0_4px_14px_rgba(43,238,108,0.4)] hover:shadow-[0_6px_20px_rgba(43,238,108,0.5)] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{isLoading ? 'Envoi...' : 'Recevoir le code'}</span>
              </button>
            </form>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Footer */}
            <div className="p-6 pb-8 border-t border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/5 backdrop-blur-sm rounded-t-3xl mt-2">
              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white transition-colors"
                >
                  ← Retour à la connexion par mot de passe
                </Link>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <AppHeader title="Vérification" onBack={() => setStep('request')} />
          {/* Verify OTP Step */}
          <div className="flex-1 flex flex-col items-center justify-center p-6 pb-20">
            {/* Icon */}
            <div className="mb-8 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
                <MaterialIcon 
                  icon="lock" 
                  className="text-primary"
                  style={{ fontSize: 40 }}
                />
              </div>
            </div>

            {/* Title */}
            <h1 className="text-slate-900 dark:text-white text-2xl font-bold text-center mb-3">
              Vérification du code
            </h1>
            <p className="text-slate-500 dark:text-gray-400 text-base text-center mb-8 max-w-sm">
              Nous avons envoyé un code de sécurité à 6 chiffres à l'adresse{' '}
              <strong className="text-slate-700 dark:text-gray-300">{email}</strong>
            </p>

            {/* OTP Input */}
            <form onSubmit={verifyForm.handleSubmit(onVerifyOTP)} className="w-full max-w-sm space-y-6">
              <div className="flex gap-2 justify-center">
                {[...Array(6)].map((_, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    maxLength={1}
                    className="w-12 h-14 text-center text-xl font-bold bg-gray-50 dark:bg-white/5 border-2 border-gray-200 dark:border-gray-700 focus:border-primary focus:ring-0 rounded-xl text-slate-900 dark:text-white transition-all outline-none"
                    value={verifyForm.watch('token')?.[index] || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!/^\d*$/.test(value)) return;

                      const currentToken = (verifyForm.getValues('token') || '').padEnd(6, ' ').split('');
                      currentToken[index] = value;
                      const newToken = currentToken.join('').trim();
                      
                      verifyForm.setValue('token', newToken);

                      // Auto-focus next
                      if (value && index < 5) {
                        const nextInput = document.getElementById(`otp-${index + 1}`);
                        nextInput?.focus();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace') {
                        if (!verifyForm.watch('token')?.[index] && index > 0) {
                          const prevInput = document.getElementById(`otp-${index - 1}`);
                          prevInput?.focus();
                        }
                      }
                    }}
                  />
                ))}
              </div>

              {verifyForm.formState.errors.token && (
                <p className="text-sm text-red-600 text-center">{verifyForm.formState.errors.token.message}</p>
              )}

              {/* Timer */}
              <div className="flex items-center justify-center gap-2 opacity-70">
                <MaterialIcon icon="schedule" style={{ fontSize: 16 }} />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Le code expire dans {countdown > 0 ? `${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, '0')}` : '02:59'}
                </span>
              </div>

              {/* Resend */}
              <div className="text-center">
                {countdown > 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Renvoyer le code dans {countdown}s
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    className="text-sm text-emerald-600 dark:text-primary hover:underline font-medium"
                    disabled={isLoading}
                  >
                    Je n'ai pas reçu le code
                  </button>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary hover:bg-[#25d360] active:scale-[0.98] transition-all duration-200 text-[#102216] text-base font-bold h-14 rounded-xl shadow-[0_4px_14px_rgba(43,238,108,0.4)] hover:shadow-[0_6px_20px_rgba(43,238,108,0.5)] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{isLoading ? 'Vérification...' : 'Valider'}</span>
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default OTPLoginPage;
